import Layout from '@src/components/Layout';
import React, { useEffect, useState } from 'react';
import { Input } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import notify from '~/components/Notify';
import i18n from '~/components/i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import LocalizedFormat from 'dayjs/plugin/utc';
import { useDebounce } from 'use-lodash-debounce';
import { FarmerUsersQuery } from '~/QueryHooks/FarmerUsersQuery';
import { UsersQuery } from '~/QueryHooks/UsersQuery';
import { RouteTypes } from '~/routes/constants';
import { StyledTab } from '~/styles/table';
import TableComponent from '~/components/TableComponent';
import Dialog from '~/components/Dialog';
import { useNavigate } from 'react-router-dom';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import UsersService from '~/api/services/users';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    sorted: 'desc',
    type: 'calendar'
  },
  {
    name: i18n.t('Name'),
    key: 'name',
    sorted: 'asc'
  },
  {
    name: i18n.t('Email'),
    key: 'email',
    sorted: 'asc'
  },
  {
    name: i18n.t('Mobile number'),
    key: 'mobileNumber',
    sorted: 'asc'
  },
  {
    name: i18n.t('Last login'),
    key: 'lastLogin',
    type: 'date',
    format: 'dddd, D MMMM YYYY HH:mm',
    missingResultMessage: i18n.t('Never logged'),
    sorted: 'asc'
  },

  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    link: RouteTypes.FARMER_USERS_EDIT,
    actionType: 'edit'
  },
  {
    popupMessage: i18n.t('Unblock'),
    type: 'icon',
    name: 'checkmark',
    size: 'large',
    color: 'green',
    actionType: 'blockUnblock',
    display: [0]
  },
  {
    popupMessage: i18n.t('Send password reset link'),
    type: 'icon',
    name: 'mail',
    size: 'large',
    actionType: 'mail',
    verifiedAction: 1,
    verifiedActionKey: 'verified'
  },
  {
    popupMessage: i18n.t('Resend password reset link'),
    type: 'icon',
    name: 'linkify',
    size: 'large',
    actionType: 'resend',
    verifiedAction: 0,
    verifiedActionKey: 'verified'
  },
  {
    popupMessage: i18n.t('Block'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'blockUnblock',
    display: [1]
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    verifiedAction: 0,
    verifiedActionKey: 'verified'
  }
]

const getActionsForLightUser = () => [
  {
    popupMessage: i18n.t('Show'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.USERS_SHOW
  }
]

const ACTIVE_FARMER_USERS_KEY = 'active_farmer_users'

const FarmerUsers: React.FunctionComponent = () => {
  const { t } = useTranslation();
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    column: '',
    sort: '',
    type: 'active_farmer_users'
  });
  const [search, setSearch] = useState('');
  const [blockFarmerUser, setBlockFarmerUser] = useState(null);
  const [loadingModalAction, setLoadingModalAction] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [deleteUserData, setDeleteUserData] = useState(null)
  const debouncedSearchValue = useDebounce(search, 300);
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const navigate = useNavigate()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = (roleSlug === LIGHT_USER_ROLE_SLUG)

  const {
    isLoading: isBusyData,
    data: farmerUsersData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = FarmerUsersQuery.getFarmerUsersIndex({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const onBlockUserSuccess = () => {
    notify(t('User successfully blocked'), 'success');
    fetchFilteredData();
  };

  const onUnblockUserSuccess = () => {
    notify(t('User successfully unblocked'), 'success');
    fetchFilteredData();
  };

  const onSendPasswordResetLinkSuccess = () => {
    notify(t('Password reset link successfully sent to user'), 'success');
  };

  const { isLoading: isBlockUserLoading, mutate: blockUserById } = UsersQuery.blockUserById(
    onBlockUserSuccess
  );

  const { isLoading: isUnblockUserLoading, mutate: unblockUserById } = UsersQuery.unblockUserById(
    onUnblockUserSuccess
  );

  const { isLoading: isSendPasswordResetLinkLoading, mutate: sendPasswordResetLinkToUserById } =
    UsersQuery.sendPasswordResetLinkToUserById(onSendPasswordResetLinkSuccess,);

  const {
    data: farmerUsersOptions,
    refetch: refetchOptions,
    isLoading: isBusyUsersOptions
  } = FarmerUsersQuery.getFarmerUsersListOptions();

  const {
    isLoading: isBusyUsersCount,
    data: farmerUsersCount,
    refetch: fetchCount
  } = FarmerUsersQuery.getFarmerUsersCount({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  useEffect(() => {
    if (farmerUsersOptions) {
      setActiveTabIndex(farmerUsersOptions?.types.findIndex(type => type.key === ACTIVE_FARMER_USERS_KEY))
    }
  }, [farmerUsersOptions])

  useEffect(() => {
    dayjs.extend(LocalizedFormat);
    dayjs.extend(timezone);
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    refetchOptions()
  }, [t]);

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
      fetchCount()
    }
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  async function resendPaswordResetLink(id) {
    try {
      await UsersService.resendActivation(id)
      notify(t('Successfully sent reset link'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'blockUnblock':
        return setBlockFarmerUser(row)
      case 'mail':
        return sendPasswordResetLinkToUserById(row.id)
      case 'edit':
        return navigate(RouteTypes.FARMER_USERS_EDIT.replace(':farmerId', row.farmerId).replace(':id', row.id))
      case 'resend':
        return resendPaswordResetLink(row.id)
      case 'delete':
        return setDeleteUserData(row)
      default:
        return
    }
  }

  function renderContent() {
    return <TableComponent
      actions={lightUserLogged ? getActionsForLightUser() : getActions()}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      rows={rows}
      total={filtered}
    />
  }

  const handleBlockUser = (farmer) => {
    if (isBlockUserLoading || isUnblockUserLoading) {
      return;
    }

    if (farmer.status === 1) {
      blockUserById(farmer.id);
    } else if (farmer.status === 0) {
      unblockUserById(farmer.id);
    }
    setBlockFarmerUser(null);
  };

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      setActiveTabIndex(activeIndex)
      const status = farmerUsersOptions?.types?.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        type: status?.key,
        page: 0
      });
    }
  }

  function renderPanes() {
    const panes = farmerUsersOptions?.types?.map((option) => {
      const count = farmerUsersCount ? farmerUsersCount[`${option.key}`] : 0;
      return {
        menuItem: `${option.label}  (${count})`,
        render: () => renderContent()
      };
    });

    return (
      <StyledTab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTabIndex}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderBlockModal() {
    const action = blockFarmerUser.status === 1 ? t('Block') : t('Unblock')
    const name = blockFarmerUser?.name
    return <Dialog
      isDialogOpen={Boolean(blockFarmerUser)}
      textClose={t('Close')}
      handleClose={() => setBlockFarmerUser(null)}
      textSubmit={t(`${blockFarmerUser.status === 1 ? 'Block' : 'Unblock'}`)}
      handleSubmit={() => handleBlockUser(blockFarmerUser)}
      textContent={t('Do you want to {{action}} {{name}}?', { action, name })}
      textHeader={t('{{action}} {{name}}', { action, name })}
      type={'abort'}
    />
  }

  async function handleDeleteUser() {
    setLoadingModalAction(true)
    try {
      await UsersService.deleteUser(deleteUserData?.id)
      fetchFilteredData()
      setDeleteUserData(null)
      notify(t('Successfully deleted farmer user'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingModalAction(false)
  }

  function renderDeleteModal() {
    const name = deleteUserData?.name || ''
    return <Dialog
      isDialogOpen={Boolean(deleteUserData)}
      isLoading={loadingModalAction}
      disableSubmit={loadingModalAction}
      textClose={t('Close')}
      handleClose={() => setDeleteUserData(null)}
      textContent={t('Do you want to delete {{name}}?', { name })}
      type={'abort'}
      textSubmit={t('Delete')}
      textHeader={t('Delete farmer user {{name}}', { name })}
      handleSubmit={() => handleDeleteUser()}
    />
  }

  const { filtered, rows } = farmerUsersData ? farmerUsersData : { filtered: 0, rows: [] };
  const loading = isBusyData || isBusyUsersOptions || isBusyUsersCount;

  return (
    <Layout
      segmented
      isBusy={loading}
      title={t('Farmer Users')}
      filters={renderSearch()}
    >
      {renderPanes()}
      {Boolean(deleteUserData) && renderDeleteModal()}
      {Boolean(blockFarmerUser) && renderBlockModal()}
    </Layout>
  );
};

export default FarmerUsers;
